import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateSecurityIssueByDisplayId } from "@/hooks/useSecurityIssues";
import { useOrganizationMembers } from "@/hooks/useOrganization";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import type { SecurityIssue } from "@/types/security";
import { cn } from "@/lib/utils";

const UNASSIGNED_VALUE = "__unassigned__";

interface IssueAssignSelectProps {
  issue: SecurityIssue;
}

export function IssueAssignSelect({ issue }: IssueAssignSelectProps) {
  const mutation = useUpdateSecurityIssueByDisplayId();
  const { organization } = useOrganizationContext();
  const { data: members } = useOrganizationMembers(organization?.id);

  // Derived state pattern: show optimistic value while mutation is pending
  const currentAssigneeId = mutation.isPending
    ? mutation.variables!.data.assigned_to_user_id ?? null
    : issue.assigned_to_user_id ?? null;

  // Find the display name for the current assignee
  const currentAssigneeName = (() => {
    if (currentAssigneeId === null) return null;
    if (mutation.isPending) {
      const member = members?.find((m) => m.id === currentAssigneeId);
      return member?.name ?? null;
    }
    return issue.assigned_to_name ?? null;
  })();

  const handleAssigneeChange = (value: string) => {
    const newAssigneeId = value === UNASSIGNED_VALUE ? null : Number(value);

    // Skip if the value hasn't changed
    if (newAssigneeId === (issue.assigned_to_user_id ?? null)) return;

    mutation.mutate({
      displayId: issue.id,
      data: { assigned_to_user_id: newAssigneeId },
    });
  };

  const selectValue = currentAssigneeId !== null ? String(currentAssigneeId) : UNASSIGNED_VALUE;

  return (
    <Select value={selectValue} onValueChange={handleAssigneeChange}>
      <SelectTrigger
        className={cn(
          "w-auto h-auto border rounded-full px-2.5 py-0.5 text-xs font-medium gap-1",
          currentAssigneeId !== null
            ? "bg-blue-100 text-blue-800 border-blue-300"
            : "bg-gray-100 text-gray-600 border-gray-300",
          mutation.isPending && "opacity-70"
        )}
      >
        <SelectValue>
          {currentAssigneeName ?? "Unassigned"}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={UNASSIGNED_VALUE}>
          <span className="text-gray-500">Unassigned</span>
        </SelectItem>
        {members?.map((member) => (
          <SelectItem key={member.id} value={String(member.id)}>
            <div className="flex items-center gap-2">
              <span>{member.name}</span>
              <span className="text-xs text-muted-foreground">{member.email}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
